import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useAppSelector } from '@state/hooks'
import { useTabNavigation } from '@contexts/TabNavigationContext'
import { CanvasHeaderStrip } from './CanvasHeaderStrip'
import { CanvasPerspectiveRow } from './CanvasPerspectiveRow'
import {
  aggregateByPerspective,
  dedupCausalLinks,
  extractObjectives,
  groupByBscRow,
  type CanvasCausalLink,
  type CascadeViewMode,
} from './strategyMapHelpers'
import './CanvasKNFrame.css'

interface Props {
  viewMode: CascadeViewMode
}

export const CanvasKNFrame: React.FC<Props> = ({ viewMode }) => {
  // Kept in the prop chain for restoration when CanvasCascadeOverlay is re-mounted.
  void viewMode

  const strategyMap = useAppSelector(s => {
    const activeId = s.conversations.activeConversationId
    if (!activeId) return undefined
    const conv = s.conversations.conversations.find(c => c.id === activeId)
    return conv?.strategyWorkspaceData?.strategyMap
  })

  const objectives = useMemo(() => extractObjectives(strategyMap), [strategyMap])
  const rows = useMemo(() => groupByBscRow(objectives), [objectives])
  const links: CanvasCausalLink[] = useMemo(() => dedupCausalLinks(strategyMap), [strategyMap])
  const scorecardByPerspective = useMemo(
    () => aggregateByPerspective(objectives),
    [objectives],
  )

  const [focusedCardId, setFocusedCardId] = useState<string | null>(null)

  const chainNeighborIds = useMemo(() => {
    if (!focusedCardId) return new Set<string>()
    const set = new Set<string>()
    for (const link of links) {
      if (link.source === focusedCardId) set.add(link.target)
      if (link.target === focusedCardId) set.add(link.source)
    }
    return set
  }, [focusedCardId, links])

  const handleCardClick = useCallback((id: string) => {
    setFocusedCardId(prev => (prev === id ? null : id))
  }, [])

  const handleGridClick = useCallback((e: React.MouseEvent) => {
    // Only clear focus when the click target IS the grid container itself.
    // Cards stop propagation in onClick (see CanvasObjectiveCard), but
    // structural children (perspective rows, scorecard cells, banners, the
    // CVP strip) do NOT — clicks on those bubble here but should not clear
    // focus. The e.target === e.currentTarget guard keeps clear-focus
    // behavior limited to direct grid-background clicks.
    if (e.target === e.currentTarget) setFocusedCardId(null)
  }, [])

  const handleGridKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setFocusedCardId(null)
  }, [])

  const gridRef = useRef<HTMLDivElement>(null)
  const { navigateToTab } = useTabNavigation()

  const navigateToRegisterRow = (slug: string) => {
    navigateToTab('report')
    setTimeout(() => {
      window.location.hash = slug
      const el = document.getElementById(slug)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 300)
  }

  const handleMeasureClick = (name: string) => {
    const slug = `measure-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`
    navigateToRegisterRow(slug)
  }

  const handleInitiativeClick = (name: string) => {
    const slug = `initiative-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`
    navigateToRegisterRow(slug)
  }

  const findRow = (p: typeof rows[number]['perspective']) =>
    rows.find(r => r.perspective === p) ?? { perspective: p, objectives: [] }

  return (
    <div className="canvas-kn-frame">
      <CanvasHeaderStrip />
      <div
        className="canvas-kn-grid"
        ref={gridRef}
        onClick={handleGridClick}
        onKeyDown={handleGridKeyDown}
        tabIndex={-1}
      >
        <div className="canvas-kn-banner-row">
          <span className="canvas-kn-grid-col-empty" aria-hidden="true" />
          <div className="canvas-kn-bosm-banner">
            Business Objectives and Strategy Map
          </div>
          <div className="canvas-kn-col-label-pill">Measures</div>
          <div className="canvas-kn-col-label-pill">Targets</div>
          <div className="canvas-kn-col-label-pill">Initiatives</div>
        </div>
        <div className="canvas-kn-ltbv-row">
          <span className="canvas-kn-grid-col-empty" aria-hidden="true" />
          <div className="canvas-kn-apex-anchor" aria-label="Strategy apex">
            <span className="canvas-kn-apex-label">Long-term Business Value</span>
          </div>
          <span className="canvas-kn-grid-col-empty" aria-hidden="true" />
          <span className="canvas-kn-grid-col-empty" aria-hidden="true" />
          <span className="canvas-kn-grid-col-empty" aria-hidden="true" />
        </div>
        <div className="canvas-kn-strategy-header-row">
          <span className="canvas-kn-strategy-header-spacer" aria-hidden="true" />
          <div className="canvas-kn-strategy-header-body">
            <div className="canvas-kn-strategy-header-label canvas-kn-strategy-header-label--productivity">
              <span className="canvas-kn-strategy-header-main">Productivity Strategy</span>
              <span className="canvas-kn-strategy-header-suffix"> (shorter-term)</span>
            </div>
            <div className="canvas-kn-strategy-header-label canvas-kn-strategy-header-label--growth">
              <span className="canvas-kn-strategy-header-main">Growth Strategy</span>
              <span className="canvas-kn-strategy-header-suffix"> (longer-term)</span>
            </div>
          </div>
          <span className="canvas-kn-strategy-header-spacer" aria-hidden="true" />
          <span className="canvas-kn-strategy-header-spacer" aria-hidden="true" />
          <span className="canvas-kn-strategy-header-spacer" aria-hidden="true" />
        </div>
        <CanvasPerspectiveRow
          perspective="Financial"
          objectives={findRow('Financial').objectives}
          scorecard={scorecardByPerspective['Financial']}
          onMeasureClick={handleMeasureClick}
          onInitiativeClick={handleInitiativeClick}
          focusedCardId={focusedCardId}
          chainNeighborIds={chainNeighborIds}
          onCardClick={handleCardClick}
        />
        <CanvasPerspectiveRow
          perspective="Customer"
          objectives={findRow('Customer').objectives}
          scorecard={scorecardByPerspective['Customer']}
          onMeasureClick={handleMeasureClick}
          onInitiativeClick={handleInitiativeClick}
          focusedCardId={focusedCardId}
          chainNeighborIds={chainNeighborIds}
          onCardClick={handleCardClick}
        />
        <CanvasPerspectiveRow
          perspective="Internal Process"
          objectives={findRow('Internal Process').objectives}
          scorecard={scorecardByPerspective['Internal Process']}
          onMeasureClick={handleMeasureClick}
          onInitiativeClick={handleInitiativeClick}
          focusedCardId={focusedCardId}
          chainNeighborIds={chainNeighborIds}
          onCardClick={handleCardClick}
        />
        <div className="canvas-kn-creating-band">
          <div className="canvas-kn-creating-label canvas-kn-creating-label--alignment">
            Creating Alignment
          </div>
          <div className="canvas-kn-creating-label canvas-kn-creating-label--readiness">
            Creating Readiness
          </div>
          <div className="canvas-kn-creating-spacer" aria-hidden="true" />
          <div className="canvas-kn-creating-spacer" aria-hidden="true" />
          <div className="canvas-kn-creating-spacer" aria-hidden="true" />
        </div>
        <CanvasPerspectiveRow
          perspective="Learning & Growth"
          objectives={findRow('Learning & Growth').objectives}
          scorecard={scorecardByPerspective['Learning & Growth']}
          onMeasureClick={handleMeasureClick}
          onInitiativeClick={handleInitiativeClick}
          focusedCardId={focusedCardId}
          chainNeighborIds={chainNeighborIds}
          onCardClick={handleCardClick}
        />
        {/* <CanvasCascadeOverlay /> removed 2026-05-19: underlying causal_chains
            data is fan-out (every card connects to every adjacent-level card),
            so lines reveal no insight. Component + infrastructure (viewMode,
            focusedCardId, filterCausalLinks helper) remain in source — restore
            this line if the generator is ever updated to produce curated 1-2
            line-per-objective chains. See:
              docs/superpowers/specs/2026-05-19-canvas-cascade-line-presentation-design.md
              docs/superpowers/plans/2026-05-19-canvas-cascade-line-presentation.md */}
      </div>
    </div>
  )
}
